import { Megaphone, Send } from "lucide-react";
import { useState } from "react";
import { useDataProvider, useGetList, useNotify, useRefresh } from "ra-core";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { ApprovalGate } from "../approvals/ApprovalGate";
import { CampaignPerformance } from "./CampaignPerformance";
import { CampaignAssistPanel } from "./CampaignAssistPanel";
import { EmailBuilder } from "./EmailBuilder";
import {
  type EmailBlock,
  blocksToText,
  parseBlocks,
  textToBlocks,
} from "./emailBlocks";

import { getSupabaseClient } from "../../atomic-crm/providers/supabase/supabase";
import { SectionCard } from "../data-display/SectionCard";
import { StatusBadge } from "../data-display/StatusBadge";
import {
  type Campaign,
  type CampaignRecipient,
  RECIPIENT_STATUS_TONES,
  type Segment,
  type SendCampaignResult,
  TEMPLATE_TOKEN_HINT,
  describeDefinition,
  emptyDefinition,
} from "./campaignModel";
import { AudiencePreview } from "./SegmentEditor";

const inputClass =
  "h-9 w-full rounded-md border border-input bg-transparent px-2.5 text-[13px] outline-none focus-visible:ring-2 focus-visible:ring-ring";

interface ContactChoice {
  id: number;
  first_name: string | null;
  last_name: string | null;
}

/** Editor + send controls + recipient ledger for one campaign. */
export const CampaignDetail = ({
  campaign,
  segments,
}: {
  campaign: Campaign;
  segments: Segment[];
}) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const dataProvider = useDataProvider();

  const [name, setName] = useState(campaign.name);
  const [channel, setChannel] = useState(campaign.channel);
  const [subject, setSubject] = useState(campaign.subject ?? "");
  const [body, setBody] = useState(campaign.body);
  const [blocks, setBlocks] = useState<EmailBlock[]>(
    () => parseBlocks(campaign.body_blocks) ?? textToBlocks(campaign.body),
  );
  const [segmentId, setSegmentId] = useState(campaign.segment_id ?? "");
  const [folder, setFolder] = useState(campaign.folder ?? "");
  const [variantSubject, setVariantSubject] = useState(
    campaign.variant_subject ?? "",
  );
  const [variantBody, setVariantBody] = useState(campaign.variant_body ?? "");
  const [showVariant, setShowVariant] = useState(
    Boolean(campaign.variant_body),
  );
  const [scheduledAt, setScheduledAt] = useState("");
  const [busy, setBusy] = useState(false);
  const [sendResult, setSendResult] = useState<SendCampaignResult | null>(null);

  // Re-key local edit state only when the SELECTED campaign changes, never
  // on background refetches (which would wipe in-progress typing).
  const [loadedId, setLoadedId] = useState(campaign.id);
  if (loadedId !== campaign.id) {
    setLoadedId(campaign.id);
    setName(campaign.name);
    setChannel(campaign.channel);
    setSubject(campaign.subject ?? "");
    setBody(campaign.body);
    setBlocks(parseBlocks(campaign.body_blocks) ?? textToBlocks(campaign.body));
    setSegmentId(campaign.segment_id ?? "");
    setFolder(campaign.folder ?? "");
    setVariantSubject(campaign.variant_subject ?? "");
    setVariantBody(campaign.variant_body ?? "");
    setShowVariant(Boolean(campaign.variant_body));
    setScheduledAt("");
    setSendResult(null);
  }

  // For email the blocks are the source of truth and the send text is derived;
  // for SMS the textarea edits body directly. One derived value keeps every
  // save/send path below channel-agnostic.
  const effectiveBody = channel === "email" ? blocksToText(blocks) : body;

  const editable =
    campaign.status === "draft" || campaign.status === "scheduled";
  const segment = segments.find((s) => s.id === segmentId);
  const definition = segment?.definition ?? emptyDefinition();

  const save = async (extra: Partial<Campaign> = {}) => {
    setBusy(true);
    try {
      await dataProvider.update("campaigns", {
        id: campaign.id,
        data: {
          name: name.trim() || "Untitled campaign",
          channel,
          subject: subject.trim() || null,
          body: effectiveBody,
          body_blocks: channel === "email" ? blocks : null,
          variant_subject: variantSubject.trim() || null,
          variant_body: showVariant ? variantBody.trim() || null : null,
          segment_id: segmentId || null,
          folder: folder.trim() || null,
          ...extra,
        },
        previousData: campaign,
      });
      refresh();
      return true;
    } catch (error) {
      notify(error instanceof Error ? error.message : "Could not save", {
        type: "error",
      });
      return false;
    } finally {
      setBusy(false);
    }
  };

  const sendNow = async () => {
    if (!effectiveBody.trim()) {
      notify("Write the campaign message first", { type: "warning" });
      return;
    }
    if (!(await save())) return;
    setBusy(true);
    const { data, error } = await getSupabaseClient().rpc("send_campaign", {
      p_campaign_id: campaign.id,
    });
    setBusy(false);
    if (error) {
      notify(error.message, { type: "error" });
      return;
    }
    const result = (Array.isArray(data) ? data[0] : data) as SendCampaignResult;
    setSendResult(result);
    notify(
      `Sent to ${result.recipients} contact(s), ${result.queued} queued, ` +
        `${result.suppressed} suppressed, ${result.failed} failed`,
      { type: result.failed > 0 ? "warning" : "info" },
    );
    refresh();
  };

  const schedule = async () => {
    if (!scheduledAt) {
      notify("Pick a date and time first", { type: "warning" });
      return;
    }
    if (!effectiveBody.trim()) {
      notify("Write the campaign message first", { type: "warning" });
      return;
    }
    const when = new Date(scheduledAt);
    if (when.getTime() <= Date.now()) {
      notify("Scheduled time must be in the future", { type: "warning" });
      return;
    }
    if (
      await save({
        status: "scheduled",
        scheduled_at: when.toISOString(),
      } as Partial<Campaign>)
    ) {
      notify(`Scheduled for ${when.toLocaleString()}`);
    }
  };

  const cancelSchedule = async () => {
    if (
      await save({ status: "draft", scheduled_at: null } as Partial<Campaign>)
    ) {
      notify("Schedule cancelled, back to draft");
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <SectionCard
        icon={Megaphone}
        title={campaign.name}
        subtitle={
          campaign.status === "scheduled" && campaign.scheduled_at
            ? `Scheduled for ${new Date(campaign.scheduled_at).toLocaleString()}`
            : campaign.sent_at
              ? `Sent ${new Date(campaign.sent_at).toLocaleString()}`
              : "Draft, nothing is sent until you say so"
        }
      >
        <div className="flex flex-col gap-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="flex flex-col gap-1 text-[12.5px] font-medium">
              Name
              <input
                className={inputClass}
                value={name}
                disabled={!editable}
                onChange={(e) => setName(e.target.value)}
              />
            </label>
            <label className="flex flex-col gap-1 text-[12.5px] font-medium">
              Channel
              <select
                className={inputClass}
                value={channel}
                disabled={!editable}
                onChange={(e) =>
                  setChannel(e.target.value as Campaign["channel"])
                }
              >
                <option value="email">Email</option>
                <option value="sms">SMS</option>
              </select>
            </label>
          </div>

          <label className="flex flex-col gap-1 text-[12.5px] font-medium">
            Folder (optional)
            <input
              className={inputClass}
              placeholder="Fall promos"
              list="gravun-campaign-folders"
              value={folder}
              disabled={!editable}
              onChange={(e) => setFolder(e.target.value)}
            />
          </label>

          {channel === "email" && (
            <label className="flex flex-col gap-1 text-[12.5px] font-medium">
              Subject
              <input
                className={inputClass}
                placeholder="Fall tune-up special for {{first_name}}"
                value={subject}
                disabled={!editable}
                onChange={(e) => setSubject(e.target.value)}
              />
            </label>
          )}

          {channel === "email" ? (
            <div className="flex flex-col gap-1">
              <span className="text-[12.5px] font-medium">Message</span>
              <EmailBuilder
                blocks={blocks}
                onChange={setBlocks}
                disabled={!editable}
              />
              <span className="text-[11.5px] text-muted-foreground">
                {TEMPLATE_TOKEN_HINT}
              </span>
            </div>
          ) : (
            <label className="flex flex-col gap-1 text-[12.5px] font-medium">
              Message
              <textarea
                className={cn(inputClass, "h-32 resize-y py-2")}
                placeholder={`Hi {{first_name}}, …`}
                value={body}
                disabled={!editable}
                onChange={(e) => setBody(e.target.value)}
              />
              <span className="text-[11.5px] font-normal text-muted-foreground">
                {TEMPLATE_TOKEN_HINT}
              </span>
            </label>
          )}

          {editable ? <CampaignAssistPanel campaignId={campaign.id} /> : null}

          {showVariant ? (
            <div className="flex flex-col gap-2 rounded-md border border-dashed border-border p-3">
              <div className="flex items-center justify-between">
                <span className="text-[12.5px] font-medium">
                  Variant B, half the audience gets this version
                </span>
                {editable ? (
                  <button
                    type="button"
                    className="text-[11.5px] text-muted-foreground hover:text-foreground"
                    onClick={() => setShowVariant(false)}
                  >
                    Remove test
                  </button>
                ) : null}
              </div>
              {channel === "email" ? (
                <input
                  className={inputClass}
                  placeholder="Variant B subject (blank = same as A)"
                  value={variantSubject}
                  disabled={!editable}
                  onChange={(e) => setVariantSubject(e.target.value)}
                />
              ) : null}
              <textarea
                className={cn(inputClass, "h-20 resize-y py-2")}
                placeholder={`Version B: Hi {{first_name}}, …`}
                value={variantBody}
                disabled={!editable}
                onChange={(e) => setVariantBody(e.target.value)}
              />
              <span className="text-[11.5px] text-muted-foreground">
                The split is 50/50 and deterministic per contact. Results show
                side by side after the send, with the sample size, because a
                12-vs-9 click split is not a verdict.
              </span>
            </div>
          ) : editable ? (
            <button
              type="button"
              className="w-fit text-[11.5px] text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
              onClick={() => setShowVariant(true)}
            >
              + Add an A/B variant
            </button>
          ) : null}

          <label className="flex flex-col gap-1 text-[12.5px] font-medium">
            Audience
            <select
              className={inputClass}
              value={segmentId}
              disabled={!editable}
              onChange={(e) => setSegmentId(e.target.value)}
            >
              <option value="">All contacts</option>
              {segments.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}, {describeDefinition(s.definition)}
                </option>
              ))}
            </select>
          </label>

          {editable && (
            <ApprovalGate
              actionType="campaign.send"
              subjectTable="campaigns"
              subjectId={campaign.id}
              summary={`Send campaign "${campaign.name}" (${campaign.channel})`}
            />
          )}
          {editable && (
            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border-subtle pt-3">
              <div className="flex items-center gap-2">
                <input
                  type="datetime-local"
                  className={cn(inputClass, "w-56")}
                  value={scheduledAt}
                  onChange={(e) => setScheduledAt(e.target.value)}
                />
                {campaign.status === "scheduled" ? (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={cancelSchedule}
                  >
                    Cancel schedule
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={schedule}
                  >
                    Schedule
                  </Button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busy}
                  onClick={() => save()}
                >
                  Save draft
                </Button>
                <Button size="sm" disabled={busy} onClick={sendNow}>
                  <Send className="mr-1 size-3.5" />
                  Send now
                </Button>
              </div>
            </div>
          )}

          {sendResult && (
            <p className="text-[12.5px] text-muted-foreground">
              {sendResult.queued} queued · {sendResult.suppressed} suppressed
              (consent) · {sendResult.failed} failed · {sendResult.logged}{" "}
              logged
            </p>
          )}
        </div>
      </SectionCard>

      {editable && (
        <AudiencePreview definition={definition} channel={channel} />
      )}

      {(campaign.status === "sent" || campaign.status === "sending") && (
        <>
          <CampaignPerformance campaign={campaign} />
          <RecipientsPanel campaignId={campaign.id} />
        </>
      )}
    </div>
  );
};

/** Per-contact send ledger, every refusal is visible, never silent. */
const RecipientsPanel = ({ campaignId }: { campaignId: string }) => {
  const { data: recipients } = useGetList<CampaignRecipient>(
    "campaign_recipients",
    {
      pagination: { page: 1, perPage: 500 },
      sort: { field: "created_at", order: "ASC" },
      filter: { campaign_id: campaignId },
    },
  );
  const { data: contacts } = useGetList<ContactChoice>("contacts_summary", {
    pagination: { page: 1, perPage: 500 },
    sort: { field: "first_name", order: "ASC" },
  });

  const contactName = (id: number) => {
    const contact = contacts?.find((c) => c.id === id);
    return contact
      ? `${contact.first_name ?? ""} ${contact.last_name ?? ""}`.trim()
      : `#${id}`;
  };

  const counts = (recipients ?? []).reduce<Record<string, number>>(
    (acc, r) => ({ ...acc, [r.status]: (acc[r.status] ?? 0) + 1 }),
    {},
  );

  return (
    <SectionCard
      title="Recipients"
      subtitle={Object.entries(counts)
        .map(([status, count]) => `${count} ${status}`)
        .join(" · ")}
    >
      <div className="max-h-72 overflow-y-auto">
        {(recipients ?? []).map((recipient) => (
          <div
            key={recipient.id}
            className="flex items-center justify-between gap-3 border-b border-border-subtle py-2 text-[13px] last:border-0"
          >
            <div className="min-w-0">
              <p className="truncate font-medium">
                {contactName(recipient.contact_id)}
              </p>
              {recipient.error && (
                <p className="truncate text-[11.5px] text-muted-foreground">
                  {recipient.error}
                </p>
              )}
            </div>
            <StatusBadge
              label={recipient.status}
              tone={RECIPIENT_STATUS_TONES[recipient.status] ?? "neutral"}
            />
          </div>
        ))}
      </div>
    </SectionCard>
  );
};
